import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Group, Modal, MultiSelect, Stack, Text, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { ApiError, type SchedTask, type ScheduleSummary, scheduleApi } from '../../api';

/** What the dialog works on: a new summary (with a first choice of tasks), or an existing one. */
export type SummaryTarget =
  | { mode: 'create'; name: string; taskIds: string[] }
  | { mode: 'edit'; summary: ScheduleSummary };

/**
 * A summary of the simplified plan: its name and which tasks it stands for.
 * A task can be in one summary only; choosing it here takes it out of
 * another. Changes the view, never the dates of the detailed plan.
 */
export function SummaryModal({ projectId, target, tasks, summaries, onClose, onDone }: {
  projectId: string;
  target: SummaryTarget | null;
  tasks: readonly SchedTask[];
  summaries: readonly ScheduleSummary[];
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  return (
    <Modal opened={target !== null} onClose={onClose} size="lg" title={target?.mode === 'edit' ? 'Zusammenfassung bearbeiten' : 'Vorgänge zusammenfassen'}>
      {target && <Body projectId={projectId} target={target} tasks={tasks} summaries={summaries} onClose={onClose} onDone={onDone} />}
    </Modal>
  );
}

/** Mounted afresh for every opening, so a reload of the plan behind it keeps what was typed. */
function Body({ projectId, target, tasks, summaries, onClose, onDone }: {
  projectId: string;
  target: SummaryTarget;
  tasks: readonly SchedTask[];
  summaries: readonly ScheduleSummary[];
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(() => (target.mode === 'create' ? target.name : target.summary.name));
  const [taskIds, setTaskIds] = useState<string[]>(() => (target.mode === 'create' ? target.taskIds : tasks.filter((t) => t.summaryId === target.summary.id).map((t) => t.id)));
  const save = useMutation({
    mutationFn: () => (target.mode === 'edit'
      ? scheduleApi.updateSummary(projectId, target.summary.id, { name: name.trim(), taskIds })
      : scheduleApi.createSummary(projectId, { name: name.trim(), taskIds })),
    onSuccess: async () => { await onDone(); onClose(); },
  });
  const other = new Map(summaries.map((s) => [s.id, s.name]));
  const own = target.mode === 'edit' ? target.summary.id : null;
  const moving = taskIds.filter((id) => { const t = tasks.find((x) => x.id === id); return t?.summaryId && t.summaryId !== own; }).length;
  const options = [...tasks]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'de', { numeric: true }))
    .map((t) => ({ value: t.id, label: `${t.code}  ${t.name}${t.summaryId && t.summaryId !== own ? `  (in „${other.get(t.summaryId) ?? '…'}“)` : ''}` }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (name.trim() && taskIds.length) save.mutate(); }}>
      <Stack gap="sm">
        {save.isError && <Alert color="red">{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        <TextInput label="Name im vereinfachten Plan" placeholder="z. B. Vorplanung" required data-autofocus value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={200} />
        <MultiSelect
          label="Vorgänge" description="erscheinen im vereinfachten Plan als ein Balken vom frühesten Anfang bis zum spätesten Ende"
          searchable clearable hidePickedOptions={false} maxDropdownHeight={280}
          data={options} value={taskIds} onChange={setTaskIds}
        />
        {moving > 0 && <Text size="xs" c="orange.8">{moving} {moving === 1 ? 'Vorgang wechselt' : 'Vorgänge wechseln'} aus einer anderen Zusammenfassung hierher.</Text>}
        <Text size="xs" c="dimmed">Der Detailplan bleibt, wie er ist; gerechnet wird immer mit allen Vorgängen.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={!name.trim() || taskIds.length === 0} loading={save.isPending}>{target.mode === 'edit' ? 'Speichern' : 'Zusammenfassen'}</Button>
        </Group>
      </Stack>
    </form>
  );
}
